import React from 'react'
import { GraphQLTaggedNode, OperationType } from 'relay-runtime'
import { useBackgroundQuery } from './useBackgroundQuery'

export type RelayCacheConfig = Parameters<typeof useBackgroundQuery>[2] & {
  pollInterval?: number
  pollCondition?: boolean | (() => boolean)
}

/**
 * Performs a background query, refreshed periodically, to retrieve results without suspending; similar to useBackgroundQuery.
 * The query is only executed on the client (i.e. not during SSR).
 *
 * @param query The query to execute.
 * @param variables Query input variables.
 * @param config Relay configuration.
 * @returns An object containing the response value, or undefined if it's not yet loaded.
 */
export function usePolledBackgroundQuery<T extends OperationType>(
  query: GraphQLTaggedNode,
  variables: T['variables'] = {},
  config: RelayCacheConfig = {},
): T['response'] {
  const pollInterval = Math.max(500, config.pollInterval || 1000)
  const pollCondition = config.pollCondition ?? true
  const [pollAdditionalFetchKey, setPollAdditionalFetchKey] = React.useState(0)
  const fetchKey = `${config.fetchKey || 0}-${pollAdditionalFetchKey}`

  React.useEffect(() => {
    // TODO: don't increment poll fetch key if previous query is still processing
    const intervalHandle = window.setInterval(() => {
      let shouldPoll = Boolean(pollCondition)
      if (typeof pollCondition === 'function') {
        shouldPoll = pollCondition()
      }

      if (shouldPoll) {
        setPollAdditionalFetchKey(pollAdditionalFetchKey + 1)
      }
    }, pollInterval)

    return () => window.clearInterval(intervalHandle)
  }, [pollInterval, pollAdditionalFetchKey, pollCondition])

  return useBackgroundQuery(query, variables, { ...config, fetchKey: fetchKey })
}
